import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import moment from 'moment';

import { ApiError } from '~/src/dash/api/rest';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import {
  Col,
  Cols,
  Div,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { DashboardSelector } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardSelector';
import { AuctionInsightsPeriods } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/AuctionInsightsPeriods';
import { AuctionInsightsDatasets } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/AuctionInsightsDatasets';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  AuctionInsightsDataset,
  formatAuctionInsightsTableDataIntoLVTRows,
  generateDashboardAuctionInsightsQueryParams,
  getAuctionInsightsDatasetOptions,
  getDashboardSankeyData,
} from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/configs/mainCfg';
import { AuctionInsightsFilters } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/AuctionInsightsFilters';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import {
  AuctionInsightsData,
  getDashboardBidderAuctionInsightsTableData,
  IDashboardAuctionInsightsTableRow,
  IDashboardBidderAuctionInsightsTableQueryParams,
} from '~/src/dash/apiV2/auctionInsights';
import { AuctionInsightsSankey } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/AuctionInsightsSankey';
import {
  formatSankeyData,
  getSankeyNodesToExclude,
  IEChartsSankeyData,
  IEChartsSankeyLink,
} from '~/src/dash/components/charts/Sankey/common';
import { publicaColors } from '~/src/dash/utils/colors';
import { ECElementEvent } from 'echarts';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  extractRouteParams,
  getRoutePath,
  RouteBidderSettingsExisting,
} from '~/src/dash/router/routes';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import {
  auctionInsightsTimeRanges,
  DateRange,
  defaultDateRanges,
  generateQueryDates,
  IDateRange,
  ITimeRange,
  TimeRange,
} from '~/src/dash/utils/dateTimeRanges';
import { DashboardFiltersToggle } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights/DashboardFiltersToggle';
import { MenuToggle } from '~/src/dash/components/toggles/MenuToggle';
import { CalendarIcon, RefreshIcon } from '~/src/dash/components/icons/misc-icons';
import { generateCustomDateRange } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { track } from '~/src/dash/analytics/analytics';

const LABEL_WIDTH = 72;
const LABEL_HEIGHT = 36;

// Helper function to determine which date range is currently active
const findDateTimeRangeToggleValue = (
  startDate: moment.Moment,
  endDate: moment.Moment,
  timezone: string,
): DateRange | TimeRange => {
  if (!startDate || !endDate) {
    return defaultDateRanges[0].key as DateRange;
  }
  const dateRange = defaultDateRanges.find((range) =>
    range.isWithinRange(startDate.clone().startOf('day'), endDate.clone().endOf('day'), timezone),
  );
  return (
    (dateRange?.key as DateRange) || generateCustomDateRange(endDate.diff(startDate, 'days')).key
  );
};

export const DashboardAuctionInsights = (): JSX.Element => {
  const { session, user, isDemoUser, isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { Dashboards } = useBaseConfig();
  const cfg = Dashboards.AuctionInsights;
  const { defaultState } = cfg;

  // Calculate initial dates from config
  const initialTimezone = defaultState.selectedTimezone || session.publisher.Timezone;
  const initialQueryDates = generateQueryDates(
    defaultState.selectedDateRange,
    initialTimezone,
    false,
  );

  // load state
  // const [loadState, setLoadState] = useState(LoadingStateLoading);
  // datasets
  const [selectedDataset, setSelectedDataset] = useState(defaultState.selectedDataset);
  // periods
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(defaultState.isPeriodMenuOpen);
  const [isRealtimeOnly, setIsRealtimeOnly] = useState(defaultState.isRealtimeOnly);
  const [startDate, setStartDate] = useState(initialQueryDates.startDate);
  const [endDate, setEndDate] = useState(initialQueryDates.endDate);
  const [selectedTimezone, setSelectedTimezone] = useState(initialTimezone);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(
    defaultState.defaultRealTimeRange,
  );
  // filters
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(defaultState.isFilterMenuOpen);
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [selectedChildrenFilters, setSelectedChildrenFilters] = useState<
    Record<string, Set<string | number>>
  >({});
  // sankey
  const [sankeyLoadState, setSankeyLoadState] = useState(LoadingStateLoading);
  const [lastSankeyDataTimestamp, setLastSankeyDataTimestamp] = useState<string>('');
  const [sankeyData, setSankeyData] = useState<IEChartsSankeyData>({ nodes: [], links: [] });
  const [nodesToExclude, setNodesToExclude] = useState<string[]>([]);
  // AbortController ref to cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // table
  const [tableLoadState, setTableLoadState] = useState(LoadingStateLoading);
  const [tableData, setTableData] = useState<IDashboardAuctionInsightsTableRow[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [currencyDisplayed, setCurrencyDisplayed] = useState(session.publisher.CurrencyDisplayed);
  const [order, setOrder] = useState<Order>(cfg.table.pagination.order || 'desc');
  const [orderBy, setOrderBy] = useState<string>(cfg.table.pagination.orderBy || 'revenue');
  const [page, setPage] = useState<number>(cfg.table.pagination.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.table.pagination.rowsPerPage || 10);
  // AbortController ref for table requests
  const tableAbortControllerRef = useRef<AbortController | null>(null);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // Memoized map for DemandSources options lookup
  const demandSourcesOptionsMap = useMemo(() => {
    return new Map((filters.DemandSources || []).map((opt) => [String(opt.id), opt]));
  }, [filters]);

  // Memoized adapter map for ALL demand source transformations (not just active ones)
  const demandSourceAdapterMap = useMemo(() => {
    const adapters = getHeaderBidderAdaptersForPublisher(
      '',
      true, // isDashboard
      session.publisher,
      isSuperUser,
      true, // shouldShowAll
    );
    return new Map(adapters.map((adapter) => [adapter.key, adapter.text]));
  }, [session.publisher, isSuperUser]);

  // Memoized map for BidderID to Demand Source Label (custom demand sources only)
  const bidderIdToDemandSourceMap = useMemo(() => {
    const map = new Map<number, string>();
    (filters.DemandSources || []).forEach((ds) => {
      if (ds.childrenIDs && ds.childrenIDs.length > 0) {
        ds.childrenIDs.forEach((bidderId) => {
          map.set(Number(bidderId), ds.label);
        });
      }
    });
    return map;
  }, [filters.DemandSources]);

  // Memoized current date range for display in MenuToggle and to pass to AuctionInsightsPeriods
  const currentDateRange: IDateRange | ITimeRange = useMemo(() => {
    if (isRealtimeOnly) {
      // For real-time mode, find matching time range based on selectedTimeRange
      return (
        auctionInsightsTimeRanges.find((r) => r.key === selectedTimeRange) ||
        auctionInsightsTimeRanges[0]
      );
    } else {
      // For date range mode, use existing logic
      const rangeKey = findDateTimeRangeToggleValue(startDate, endDate, selectedTimezone);
      return (
        defaultDateRanges.find((r) => r.key === rangeKey) ||
        generateCustomDateRange(endDate.diff(startDate, 'days'))
      );
    }
  }, [startDate, endDate, selectedTimezone, isRealtimeOnly, selectedTimeRange]);

  // Check if at least one filter is active
  const hasActiveFilters = useMemo(() => {
    return Object.values(selectedFilters).some((set) => set.size > 0);
  }, [selectedFilters]);

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];

    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};
    const tempSelectedChildrenFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          const filter = cfg.filters[filterKey];

          // Set options
          tempFilters[filterKey] = result;

          // Set selected IDs from config
          tempSelectedFilters[filterKey] = filter.selectedOptionIDs || new Set<string | number>([]);

          // Initialize children state for filters that have children capability
          if (filter.hasChildrenIDs) {
            if (tempSelectedFilters[filterKey].size > 0) {
              // Resolve children IDs using fresh options from API
              const optionsMap = new Map(result.map((opt) => [opt.id, opt]));
              const resolved = new Set<number | string>([]);

              tempSelectedFilters[filterKey].forEach((selectedID) => {
                const option = optionsMap.get(selectedID);
                if (option?.childrenIDs) {
                  option.childrenIDs.forEach((childID) => resolved.add(childID));
                }
                // Silently skip if option not found (stale data)
              });

              tempSelectedChildrenFilters[filterKey] = resolved;
            } else {
              // No selections, but initialize with empty Set for consistency
              tempSelectedChildrenFilters[filterKey] = new Set<string | number>();
            }
          }
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setSelectedFilters(tempSelectedFilters);
          setSelectedChildrenFilters(tempSelectedChildrenFilters);
          setFiltersLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setSelectedChildrenFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  // const getSankeyData = (): void => {};

  const handleFiltersMenuToggle = (): void => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
    defaultState.isFilterMenuOpen = !isFilterMenuOpen;
  };

  const handlePeriodMenuToggle = (): void => {
    setIsPeriodMenuOpen(!isPeriodMenuOpen);
    defaultState.isPeriodMenuOpen = !isPeriodMenuOpen;
  };

  const handleDatasetChange = (dataset: AuctionInsightsDataset): void => {
    setSelectedDataset(dataset);
    defaultState.selectedDataset = dataset;
  };

  const handleToggleRealtimeOnly = (): void => {
    setIsRealtimeOnly(!isRealtimeOnly);
    defaultState.isRealtimeOnly = !isRealtimeOnly;
  };

  const handleApplyTimeRange = (timeRange: TimeRange): void => {
    setSelectedTimeRange(timeRange);
    defaultState.selectedTimeRange = timeRange;
    track(
      'Dashboard Auction Insights - Realtime Range Changed',
      { isSuperUser, selectedTimeRange: timeRange },
      user.user,
    );
  };

  const handleApplyDatesChange = ({ newStartDate, newEndDate }: INewCalDates): void => {
    unstable_batchedUpdates(() => {
      setStartDate(newStartDate.clone().startOf('day'));
      setEndDate(newEndDate.clone().endOf('day'));
    });
    track('Dashboard Auction Insights - Dates Changed', { isSuperUser }, user.user);
  };

  const handleApplyTimezone = (timezone: Set<string | number>): void => {
    const newTimezone = String(Array.from(timezone)[0]);
    setSelectedTimezone(newTimezone);
    defaultState.selectedTimezone = newTimezone;
  };

  // filter functions
  const handleFilterAccept = (
    value: Set<number | string>,
    key: string,
    childrenIDs?: Set<string | number>,
  ): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    }

    track(
      'Dashboard Auction Insights - Filter Used',
      { isSuperUser, selectedFilter: key },
      user.user,
    );

    // Build filters to clear for mutual exclusion
    const currentFilterGroup = cfg.filters[key]?.mutuallyExclusiveGroup;
    const filtersToClear: Record<string, true> = {};

    if (currentFilterGroup && value.size > 0) {
      Object.keys(cfg.filters).forEach((filterKey) => {
        if (
          filterKey !== key &&
          cfg.filters[filterKey].mutuallyExclusiveGroup === currentFilterGroup
        ) {
          filtersToClear[filterKey] = true;
          // Update config persistence
          cfg.filters[filterKey].selectedOptionIDs = new Set<string | number>();
        }
      });
    }

    // Create new selectedFilters object with proper immutability
    const newSelectedFilters = {
      ...selectedFilters,
      [key]: value,
      // Clear mutually exclusive filters
      ...Object.fromEntries(
        Object.keys(filtersToClear).map((k) => [k, new Set<string | number>()]),
      ),
    };

    // Create new selectedChildrenFilters object
    const newSelectedChildrenFilters = {
      ...selectedChildrenFilters,
      // Update current filter's children if exists
      ...(cfg.filters[key].hasChildrenIDs && childrenIDs !== undefined
        ? { [key]: childrenIDs }
        : {}),
      // Clear children for mutually exclusive filters
      ...Object.fromEntries(
        Object.keys(filtersToClear)
          .filter((k) => cfg.filters[k].hasChildrenIDs)
          .map((k) => [k, new Set<string | number>()]),
      ),
    };

    unstable_batchedUpdates(() => {
      setSelectedFilters(newSelectedFilters);
      setSelectedChildrenFilters(newSelectedChildrenFilters);
    });

    // Persist only selected IDs (not children - they are derived on mount)
    cfg.filters[key].selectedOptionIDs = value;
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    const clearedSelectedChildrenFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const initialDefaultIDs = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = initialDefaultIDs;
      cfg.filters[filter.key].defaultSelectedOptionIDs = initialDefaultIDs;
      if (filter.hasChildrenIDs) {
        clearedSelectedChildrenFilters[filter.key] = new Set<string | number>([]);
      }
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters(clearedSelectedFilters);
      setSelectedChildrenFilters(clearedSelectedChildrenFilters);
    });
  };

  const handleNodeClick = useCallback((params: ECElementEvent): void => {
    const excludedNodes = getSankeyNodesToExclude(
      params.name,
      cfg.datasets[selectedDataset].nodesConfig,
    );
    setNodesToExclude(excludedNodes);
  }, []);

  const handleLinkClick = useCallback((params: { data: IEChartsSankeyLink }): void => {
    const sourceKey = params.data?.source;
    if (sourceKey) {
      const excludedNodes = getSankeyNodesToExclude(
        sourceKey,
        cfg.datasets[selectedDataset].nodesConfig,
      );
      setNodesToExclude(excludedNodes);
    }
  }, []);

  const handleResetDrilldown = useCallback((): void => {
    setNodesToExclude([]);
  }, []);

  // TODO: NEEDS TO BE REWORKED TO HANDLE ALL DATASETS
  const getBidderSettingsRoute = (id: string): string => {
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
    cfg.table.pagination.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
    cfg.table.pagination.rowsPerPage = updatedRowsPerPage;
    cfg.table.pagination.page = 0;
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
      cfg.table.pagination.page = 0;
      cfg.table.pagination.order = defaultOrderDirection || 'asc';
      cfg.table.pagination.orderBy = key;
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
      cfg.table.pagination.page = 0;
      cfg.table.pagination.order = isAsc ? 'desc' : 'asc';
      cfg.table.pagination.orderBy = key;
    }
  };

  const fetchSankeyData = useCallback((): void => {
    const queryStart = moment().unix();
    if (filtersLoadState === LoadingStateHasData && hasActiveFilters) {
      // Abort any in-flight request from previous calls
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      const currentController = abortControllerRef.current;

      setSankeyLoadState(LoadingStateLoading);
      getDashboardSankeyData(
        session.publisher.ID,
        selectedTimezone,
        isRealtimeOnly ? moment().startOf('day') : startDate,
        isRealtimeOnly ? moment().endOf('day') : endDate,
        selectedDataset,
        selectedFilters,
        selectedChildrenFilters,
        demandSourcesOptionsMap,
        cfg,
        currentController.signal,
        isRealtimeOnly,
        isRealtimeOnly
          ? auctionInsightsTimeRanges.find((r) => r.key === selectedTimeRange)?.value.toString() ||
              ''
          : '',
      )
        .then((data: AuctionInsightsData) => {
          const formattedData = data
            ? formatSankeyData(
                data,
                nodesToExclude,
                cfg.datasets[selectedDataset].nodesConfig,
                publicaColors.lightGray,
              )
            : { nodes: [], links: [] };
          unstable_batchedUpdates(() => {
            setSankeyLoadState(LoadingStateHasData);
            setSankeyData(formattedData);
            setLastSankeyDataTimestamp(moment().format('HH:mm:ss'));
          });
          track(
            'Dashboard Auction Insights - Sankey Data Retrieved',
            { isSuperUser, queryTime: moment().unix() - queryStart },
            user.user,
          );
        })
        .catch((err: Error) => {
          // Ignore abort errors (they're expected when cancelling requests)
          if (err.name === 'AbortError') {
            return;
          }

          const apiError = err as ApiError;

          unstable_batchedUpdates(() => {
            setSankeyLoadState(LoadingStateNoData);
            setSankeyData({ nodes: [], links: [] });
            setLastSankeyDataTimestamp('');
            setSnackbarError(true);
            setSnackbarMessage(
              t(apiError.responseBody?.errorTKey || 'ConsoleErrorMsgUnexpectedError'),
            );
          });
          console.error(err);
        });
    } else if (filtersLoadState === LoadingStateHasData && !hasActiveFilters) {
      // No active filters - set to no data state
      unstable_batchedUpdates(() => {
        setSankeyLoadState(LoadingStateNoData);
        setSankeyData({ nodes: [], links: [] });
      });
    }
  }, [
    filtersLoadState,
    hasActiveFilters,
    session.publisher.ID,
    selectedTimezone,
    isRealtimeOnly,
    startDate,
    endDate,
    selectedDataset,
    selectedFilters,
    selectedChildrenFilters,
    demandSourcesOptionsMap,
    cfg,
    selectedTimeRange,
    nodesToExclude,
  ]);

  // on load, populate all filters
  useEffect(() => {
    populateFilters();
  }, []);

  // Fetch and format sankey data when filters or nodesToExclude change
  useEffect(() => {
    fetchSankeyData();
  }, [fetchSankeyData]);

  const fetchTableData = useCallback((): void => {
    const queryStart = moment().unix();
    if (filtersLoadState === LoadingStateHasData && hasActiveFilters) {
      // Abort any in-flight table request from previous calls
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      tableAbortControllerRef.current = new AbortController();
      const currentController = tableAbortControllerRef.current;

      setTableLoadState(LoadingStateLoading);

      // Build filter params using shared helper
      const filterParams = generateDashboardAuctionInsightsQueryParams(
        selectedDataset,
        selectedFilters,
        selectedChildrenFilters,
        demandSourcesOptionsMap,
        cfg,
      );

      // Build full table query params
      // When realtime mode: use today's dates as base for backend calculation
      // When date range mode: use selected dates
      const queryStartDate = isRealtimeOnly ? moment().startOf('day') : startDate;
      const queryEndDate = isRealtimeOnly ? moment().endOf('day') : endDate;

      const tableQuery = {
        PublisherID: Number(session.publisher.ID),
        CurrencyDisplayed: session.publisher.CurrencyDisplayed,
        Timezone: selectedTimezone,
        StartDate: queryStartDate.format('YYYY-MM-DD HH:mm:ss'),
        EndDate: queryEndDate.format('YYYY-MM-DD HH:mm:ss'),
        LiveDensityRange: isRealtimeOnly
          ? auctionInsightsTimeRanges.find((r) => r.key === selectedTimeRange)?.value.toString() ||
            ''
          : '',
        IsRealTimeOnly: isRealtimeOnly,
        ...filterParams,
        OrderBy: orderBy,
        OrderDirection: order === 'asc' ? 'ASC' : 'DESC',
        Limit: rowsPerPage,
        Offset: page * rowsPerPage,
      } as IDashboardBidderAuctionInsightsTableQueryParams;

      getDashboardBidderAuctionInsightsTableData(
        session.publisher.ID,
        tableQuery,
        currentController.signal,
      )
        .then((response) => {
          unstable_batchedUpdates(() => {
            setTableLoadState(LoadingStateHasData);
            setTableData(response.Data || []);
            setTotalRowCount(response.TotalCount || 0);
            setCurrencyDisplayed(response.CurrencyDisplayed);
          });
          track(
            'Dashboard Auction Insights - Table Data Retrieved',
            { isSuperUser, queryTime: moment().unix() - queryStart },
            user.user,
          );
        })
        .catch((err: Error) => {
          // Ignore abort errors (they're expected when cancelling requests)
          if (err.name === 'AbortError') {
            return;
          }

          // Extract and translate errorTKey (guaranteed to exist from rest.ts)
          const apiError = err as ApiError;
          const errorMessage = t(
            apiError.responseBody?.errorTKey || 'ConsoleErrorMsgUnexpectedError',
          );

          unstable_batchedUpdates(() => {
            setTableLoadState(LoadingStateNoData);
            setTableData([]);
            setTotalRowCount(0);
            setSnackbarError(true);
            setSnackbarMessage(errorMessage);
          });
          console.error(err);
        });
    } else if (filtersLoadState === LoadingStateHasData && !hasActiveFilters) {
      // No active filters - clear table data
      unstable_batchedUpdates(() => {
        setTableLoadState(LoadingStateNoData);
        setTableData([]);
        setTotalRowCount(0);
      });
    }
  }, [
    filtersLoadState,
    hasActiveFilters,
    selectedDataset,
    selectedFilters,
    selectedChildrenFilters,
    demandSourcesOptionsMap,
    cfg,
    session.publisher.ID,
    selectedTimezone,
    startDate,
    endDate,
    isRealtimeOnly, // Added: triggers refetch when toggling realtime mode
    selectedTimeRange, // Added: triggers refetch when changing time range in realtime mode
    orderBy,
    order,
    rowsPerPage,
    page,
  ]);

  const handleRefresh = (): void => {
    fetchSankeyData();
    fetchTableData();
  };

  // Fetch table data when filters or pagination change
  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  // Cleanup: abort any in-flight requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }
    };
  }, []);

  const rows = useMemo(
    () =>
      formatAuctionInsightsTableDataIntoLVTRows(
        tableData,
        cfg.table.columns,
        demandSourceAdapterMap,
        bidderIdToDemandSourceMap,
      ),
    [tableData, cfg.table.columns, demandSourceAdapterMap, bidderIdToDemandSourceMap],
  );

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding noVerticalPadding>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: '248px' }}>
            <DashboardSelector />
          </Col>
          <Col>
            <DashboardFiltersToggle
              numActive={Object.values(selectedFilters).filter((set) => set.size > 0).length}
              handleOnClick={handleFiltersMenuToggle}
              isOpen={isFilterMenuOpen}
            />
          </Col>
          <Col>
            <MenuToggle
              isActive={isPeriodMenuOpen}
              onClick={handlePeriodMenuToggle}
              value={currentDateRange.label}
              tKey={currentDateRange.tKey}
              SvgIcon={CalendarIcon}
              style={{ height: '30px' }}
            />
          </Col>
          {lastSankeyDataTimestamp && (
            <Col displayFlex>
              <TooltipWithTranslation
                tKey='DashboardTooltipLastUpdatedTime'
                tProps={{ timestamp: lastSankeyDataTimestamp }}
              >
                <div>
                  <TextButton size='small' weight='base' onClick={handleRefresh}>
                    <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <RefreshIcon color={publicaColors.lightBlue} />
                      </Col>
                      <Col>{t('Refresh')}</Col>
                    </Cols>
                  </TextButton>
                </div>
              </TooltipWithTranslation>
            </Col>
          )}
        </Cols>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <Div style={highlightedDivStyle} horizontalPadding smallVerticalPadding>
          <AuctionInsightsDatasets
            labelWidth={LABEL_WIDTH}
            labelHeight={LABEL_HEIGHT}
            selectedValue={selectedDataset}
            handleDatasetChange={handleDatasetChange}
            datasets={getAuctionInsightsDatasetOptions()}
          />
        </Div>
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ display: isPeriodMenuOpen || isFilterMenuOpen ? 'block' : 'none' }}
      >
        <Rows
          style={highlightedDivStyle}
          horizontalPadding
          smallVerticalPadding
          gap={spacing.small}
        >
          <Row noPadding style={{ display: isPeriodMenuOpen ? 'block' : 'none' }}>
            <AuctionInsightsPeriods
              calStartDate={startDate}
              calEndDate={endDate}
              currentDateRange={currentDateRange}
              handleApplyDatesChange={handleApplyDatesChange}
              loadState={LoadingStateHasData}
              labelWidth={LABEL_WIDTH}
              labelHeight={LABEL_HEIGHT}
              selectedTimezone={selectedTimezone}
              handleApplyTimezone={handleApplyTimezone}
              isRealtimeOnly={isRealtimeOnly}
              handleToggleRealtimeOnly={handleToggleRealtimeOnly}
              selectedTimeRange={selectedTimeRange}
              handleApplyTimeRange={handleApplyTimeRange}
            />
          </Row>
          <Row noPadding style={{ display: isFilterMenuOpen ? 'block' : 'none' }}>
            <AuctionInsightsFilters
              labelWidth={LABEL_WIDTH}
              labelHeight={LABEL_HEIGHT}
              cypressParentName='dashboard-auction-insights-filters'
              enableClear={true}
              filtersConfig={cfg.filters}
              filtersToOptionsMap={filters}
              handleClearFilters={handleClearFilters}
              handleApplySelect={handleFilterAccept}
              selectedFilters={selectedFilters}
              loadState={filtersLoadState}
              enabledFilters={cfg.datasets[selectedDataset]?.enabledFilters}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <Rows style={highlightedDivStyle} basePadding gap={spacing.small}>
          <Row noPadding>
            <AuctionInsightsSankey
              loadState={sankeyLoadState}
              data={sankeyData}
              nodesToExclude={nodesToExclude}
              hasActiveFilters={hasActiveFilters}
              handleNodeClick={handleNodeClick}
              handleLinkClick={handleLinkClick}
              handleResetDrilldown={handleResetDrilldown}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <Rows style={highlightedDivStyle} basePadding gap={spacing.small}>
          <Row noPadding>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <IconV2 name='pill-data' small />
              </Col>
              <Col displayFlex>
                <Text bold small tKey='Breakdown' singleLineLabel />
              </Col>
              <Col displayFlex>
                <TooltipWithTranslation tKey='TooltipAuctionInsightsBreakdown'>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconV2 name='info-static' color={publicaColors.veryDarkGray} small />
                  </div>
                </TooltipWithTranslation>
              </Col>
            </Cols>
          </Row>
          <Row noHorizontalPadding verticalPadding>
            <ListViewTable
              activeColumnKeys={cfg.table.activeColumnKeys}
              columns={cfg.table.columns}
              displayNameWithID={!isDemoUser}
              getItemSettingsRoute={getBidderSettingsRoute} // TODO: NEEDS TO BE REWORKED TO HANDLE ALL DATASETS
              shouldOpenLinkInNewTab
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={tableLoadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              tableName={'Breakdown'}
              totalRowCount={totalRowCount}
              className={'dashboard-auction-insights-table'}
              customProps={{ currencySymbol: convertECurrencyTypeToSymbol(currencyDisplayed) }}
            />
          </Row>
        </Rows>
      </Panel>
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => setSnackbarMessage('')}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
